import { BaseService } from './base';
import { ApiRequest } from '../lib/request';
import type { ApiResponse } from '../lib/response';
import { ApiException } from '../lib/exception';

import { CustomerCourse } from '../models/course';

export class CustomerCourseService extends BaseService<CustomerCourse> {
  override model = CustomerCourse;
  override modelName = 'customerCourse';
  override modelLabel = 'Customer Course';

  async enrol({
    idCourseRun,
    idCustomer,
    price,
    deposit,
    isEarly,
    paymentOption,
    requestChannels,
    sendEmail,
  }: {
    idCourseRun: number | string;
    idCustomer: number | string;
    price?: number | null;
    deposit?: number | null;
    isEarly?: boolean | null;
    paymentOption?: string;
    requestChannels?: string[];
    sendEmail?: boolean;
  }): Promise<ApiResponse<any>> {
    const data: Record<string, any> = {
      idCourseRun,
      idCustomer,
    };

    if (price !== undefined && price !== null) {
      data.price = price;
    }
    if (deposit !== undefined && deposit !== null) {
      data.deposit = deposit;
    }
    if (isEarly !== undefined) {
      data.isEarly = isEarly;
    }
    if (paymentOption) {
      data.paymentOption = paymentOption;
    }
    if (requestChannels?.length && paymentOption === 'request') {
      data.requestChannels = requestChannels;
    }
    if (sendEmail !== undefined) {
      data.sendEmail = sendEmail;
    }

    const request = new ApiRequest({
      method: 'POST',
      url: this.modelBaseUrl,
      data,
    });

    return await this.request(request);
  }

  async cancel(
    customerCourse: CustomerCourse,
    { sendEmail = false }: { sendEmail?: boolean } = {},
  ): Promise<ApiResponse<CustomerCourse>> {
    const request = new ApiRequest({
      method: 'PUT',
      url: `${this.modelBaseUrl}/${customerCourse.objectID()}/cancel`,
      data: { sendEmail },
    });

    return await this.requestItem(request, CustomerCourse);
  }

  async move(
    customerCourse: CustomerCourse,
    { idCourseRun }: { idCourseRun: number | string },
  ): Promise<ApiResponse<CustomerCourse>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${customerCourse.objectID()}/move`,
      data: { idCourseRun },
    });

    return await this.requestItem(request, CustomerCourse);
  }

  async availableMakeups(
    customerCourse: CustomerCourse,
    { idReservationCustomer }: { idReservationCustomer: number | string },
  ): Promise<any> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${customerCourse.objectID()}/availableMakeups`,
      query: { idReservationCustomer: String(idReservationCustomer) },
    });

    const response = await this.request(request);

    if (response.success) {
      return response.data;
    }

    throw new ApiException(response);
  }

  async bookMakeup(
    customerCourse: CustomerCourse,
    {
      idReservationCustomer,
      idReservation,
    }: {
      idReservationCustomer: number | string;
      idReservation: number | string;
    },
  ): Promise<ApiResponse<CustomerCourse>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${customerCourse.objectID()}/bookMakeup`,
      data: {
        idReservationCustomer,
        idReservation,
      },
    });

    return await this.requestItem(request, CustomerCourse);
  }
}
